// /assets, the Asset library (HD-10, DESIGN rev 2.1). Staff get the Console page below: dark by default (the shell's
// toggle), a Grid in medium density, the Expanded-row panel to view and edit a record (§2.5), modals only for creating
// (upload, new folder, rename) and confirms. Client accounts get the Portal page (pages/assets/PortalAssets.jsx): light by
// default through AppShell's PortalRail, List rows, no Grid and no panel (§3.3).
//
// Calls (the pre-HD-10 page's; same params, same bodies):
//   list      GET /api/clients; GET /api/assets?page&q&prop_type&client_id&folder_id (24 a page, Load more); GET /api/asset-folders
//   record    components/AssetRecord.jsx (GET /api/assets/:id and the record's actions)
//   upload    pages/assets/AssetUpload.jsx (presign, PUT, confirm, then GET /api/assets/:id until analysed; or upload-zip)
//   folders   POST /api/asset-folders { name, clientId }; PATCH /api/asset-folders/:id { name }; DELETE /api/asset-folders/:id
//             (Admin / Power User)
//   bulk      DELETE /api/assets/:id for each selected asset, one after another (staff accounts; the API refuses per asset)
import { useEffect, useState } from 'react';
import { Download, FolderPlus, Image as ImageIcon, MoreHorizontal, Pencil, Search, Trash2, Upload, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PROP_TYPES, plural, propLabel, userCanUpload, userIsAdmin } from '../lib/assetLibrary';
import { AssetPanel, analysisBadge } from '../components/AssetRecord';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Menu from '../ui/Menu';
import Modal from '../ui/Modal';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { useMedia } from '../shell/AppShell';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { useAssetLibrary } from './assets/useAssetLibrary';
import { useAssetUpload } from './assets/AssetUpload';
import PortalAssets from './assets/PortalAssets';

export default function AssetLibrary() {
  const { user } = useAuth();
  return user?.isExternal ? <PortalAssets /> : <ConsoleAssets />;
}

const stop = e => e.stopPropagation();

function ConsoleAssets() {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const canUp = userCanUpload(user);
  const isAdm = userIsAdmin(user);
  const lib = useAssetLibrary();
  const narrow = useMedia('(max-width: 1024px)');
  const phone = useMedia('(max-width: 600px)');
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [folderMenu, setFolderMenu] = useState(false);
  const [folderForm, setFolderForm] = useState(null);   // { mode: 'new' | 'rename', id?, name, clientId }
  const [folderBusy, setFolderBusy] = useState(false);

  const upload = useAssetUpload({
    clients: lib.clients, folderId: lib.folderId, folderName: lib.folderName,
    onAnalysed: lib.refresh, onZipDone: lib.refresh,
  });

  // A selection only holds assets still in view.
  useEffect(() => {
    setSelected(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(lib.assets.map(a => a.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [lib.assets]);
  const toggle = id => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function bulkDelete() {
    const ids = [...selected];
    if (!ids.length || bulkBusy) return;
    const ok = await confirm({
      title: `Delete ${plural(ids.length, 'asset')} permanently?`,
      body: 'The images and their thumbnails are deleted from cloud storage and leave the library. The Asset library has no recycle bin, so this cannot be undone. Assets you may not delete are skipped and named afterwards.',
      confirmLabel: `Delete ${plural(ids.length, 'asset')}`, destructive: true,
    });
    if (!ok) return;
    setBulkBusy(true);
    let deleted = 0;
    const skipped = [];
    for (const id of ids) {
      try { await api.delete(`/api/assets/${id}`); deleted++; }
      catch (err) {
        const a = lib.assets.find(x => x.id === id);
        skipped.push(`${clean(a?.fileName) || id} (${err.response?.data?.error || 'failed'})`);
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    if (ids.includes(openId)) setOpenId(null);
    lib.load();
    if (skipped.length) {
      toast(`${deleted} of ${ids.length} deleted. Skipped ${skipped.length}: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? ` and ${skipped.length - 3} more` : ''}.`, { tone: 'warn', duration: 12000 });
    } else toast(`${plural(deleted, 'asset')} deleted.`);
  }

  async function downloadRow(a) {
    try {
      const r = await api.get(`/api/assets/${a.id}/download`);
      const link = document.createElement('a');
      link.href = r.data.url; link.download = a.fileName || 'asset'; link.click();
    } catch (err) { toast(err.response?.data?.error || 'The download did not start. Try again.', { tone: 'danger' }); }
  }

  // ── Folders (Admin / Power User) ──────────────────────────────────────────────────────────────────────────────────

  const current = lib.folders.find(f => f.id === lib.folderId) || null;
  async function saveFolder() {
    const name = (folderForm?.name || '').trim();
    if (!name || folderBusy) return;
    setFolderBusy(true);
    try {
      if (folderForm.mode === 'new') {
        const r = await api.post('/api/asset-folders', { name, clientId: folderForm.clientId || null });
        lib.folderCreated(r.data);
        toast(`Folder ${clean(r.data.name)} created.`);
      } else {
        const r = await api.patch(`/api/asset-folders/${folderForm.id}`, { name });
        lib.folderRenamed(r.data);
        toast(`Folder renamed to ${clean(r.data.name)}.`);
      }
      setFolderForm(null);
    } catch (err) { toast(err.response?.data?.error || 'The folder was not saved. Try again.', { tone: 'danger' }); }
    finally { setFolderBusy(false); }
  }
  async function deleteFolder(f) {
    const ok = await confirm({
      title: `Delete the folder ${clean(f.name)}?`,
      body: 'The assets in it stay in the library, with no folder.',
      confirmLabel: 'Delete folder', destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/asset-folders/${f.id}`);
      lib.folderDeleted(f.id);
      toast(`Folder ${clean(f.name)} deleted.`);
    } catch (err) { toast(err.response?.data?.error || 'The folder was not deleted. Try again.', { tone: 'danger' }); }
  }

  // ── Header ────────────────────────────────────────────────────────────────────────────────────────────────────────

  const meta = lib.state === 'loading' ? 'Loading' : lib.state === 'error' ? undefined
    : `${plural(lib.total, 'asset')}${lib.filtered ? ' match' : ''}`;
  usePageHeader({
    title: 'Asset library',
    meta,
    actions: (
      <>
        {isAdm && <Button variant="quiet" icon={FolderPlus} onClick={() => setFolderForm({ mode: 'new', name: '', clientId: '' })}>New folder</Button>}
        {canUp && <Button variant="primary" icon={Upload} data-testid="assets-upload" onClick={upload.openUpload}>Upload</Button>}
      </>
    ),
    filters: (
      <>
        <Input aria-label="Search the asset library" icon={Search} type="search" placeholder="Search props, brands, themes" data-testid="assets-search"
          value={lib.searchInput} onChange={e => lib.setSearchInput(e.target.value)} />
        {lib.clients.length > 0 && (
          <Select aria-label="Client" value={lib.clientId} onChange={e => lib.setClientId(e.target.value)}
            options={[{ value: '', label: 'All clients' }, ...lib.clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        )}
        <Select aria-label="Folder" value={lib.folderId || ''} data-testid="assets-folder" onChange={e => lib.setFolderId(e.target.value || null)}
          options={[{ value: '', label: 'All assets' }, ...lib.folders.map(f => ({ value: f.id, label: clean(f.name) }))]} />
        {isAdm && current && (
          <Popover open={folderMenu} onOpenChange={setFolderMenu} label="Folder actions"
            trigger={({ toggle: t, ref, props }) => (
              <Button ref={ref} {...props} variant="quiet" iconOnly icon={MoreHorizontal} label={`Actions for the folder ${clean(current.name)}`} onClick={t} />
            )}>
            <Menu label="Folder actions" onClose={() => setFolderMenu(false)} items={[
              { label: 'Rename folder', icon: Pencil, onSelect: () => setFolderForm({ mode: 'rename', id: current.id, name: current.name }) },
              { label: 'Delete folder', icon: Trash2, danger: true, onSelect: () => deleteFolder(current) },
            ]} />
          </Popover>
        )}
        {lib.filtered && <Button variant="quiet" icon={X} onClick={lib.clearFilters}>Clear filters</Button>}
      </>
    ),
  });

  // ── Grid ──────────────────────────────────────────────────────────────────────────────────────────────────────────

  const panelOpen = !!openId;
  const shown = lib.assets;
  const allSelected = shown.length > 0 && shown.every(a => selected.has(a.id));
  const columns = [
    { key: 'select', width: 48,
      label: <Checkbox label={<span className="ui-sr">Select every asset shown</span>} checked={allSelected}
        onChange={e => setSelected(e.target.checked ? new Set(shown.map(a => a.id)) : new Set())} />,
      render: a => (
        <span onClick={stop}>
          <Checkbox label={<span className="ui-sr">Select {clean(a.fileName)}</span>} checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
        </span>
      ) },
    { key: 'image', label: <span className="ui-sr">Image</span>, width: 88, render: a => (a.thumbnailUrl
      ? <img className="ui-thumb" src={a.thumbnailUrl} alt="" loading="lazy" />
      : <span className="ui-thumb ui-thumb--label"><Icon icon={ImageIcon} /></span>) },
    { key: 'name', label: 'Name', width: 200, render: a => <span className="ui-truncate" title={clean(a.fileName)}>{clean(a.fileName)}</span> },
    { key: 'type', label: 'Type', width: 136, hide: phone, render: a => analysisBadge(a) || propLabel(clean(a.aiTags?.prop_type)) || <span className="ui-meta">None</span> },
    { key: 'description', label: 'Description', width: 492, hide: panelOpen || narrow, render: a => (a.aiDescription
      ? <span className="ui-truncate" title={clean(a.aiDescription)}>{clean(a.aiDescription)}</span> : null) },
    { key: 'themes', label: 'Themes', width: 236, hide: narrow, render: a => {
      const themes = (a.aiTags?.themes || []).slice(0, 3).map(clean).join(', ');
      return themes ? <span className="ui-truncate ui-text-2" title={themes}>{themes}</span> : null;
    } },
  ];
  const rowActions = a => <Button variant="quiet" iconOnly icon={Download} label={`Download ${clean(a.fileName)}`} onClick={() => downloadRow(a)} />;

  const empty = lib.searchType === 'semantic_unavailable'
    ? { message: 'No asset matches that search, and the similar-image search is not available right now. Try other words.' }
    : lib.filtered
      ? { message: 'No asset matches these filters. Try a broader word, or clear the filters.', action: { label: 'Clear filters', icon: X, onClick: lib.clearFilters } }
      : lib.folderId
        ? { message: canUp ? 'This folder is empty. Upload images into it, or move assets here from their panel.' : 'This folder is empty.' }
        : { message: canUp ? 'The library is empty. Upload prop photos to start it.' : 'The library is empty.',
            ...(canUp ? { action: { label: 'Upload', icon: Upload, onClick: upload.openUpload } } : {}) };
  const n = selected.size;
  const gridRows = lib.searchType === 'semantic_unavailable' ? [] : shown;

  return (
    <div className="ui ui-page ui-page--flush">
      <div className="ui-inset ui-inset--bar ui-toolbar">
        <span className="ui-seg ui-seg--wrap" role="group" aria-label="Prop type">
          {['', ...PROP_TYPES].map(v => (
            <Button key={v || 'all'} variant="quiet" aria-pressed={lib.propType === v} data-testid="assets-type" data-value={v || 'all'}
              onClick={() => lib.setPropType(v)}>{v ? propLabel(v) : 'All types'}</Button>
          ))}
        </span>
        {lib.searchType === 'semantic' && lib.state === 'data' && <span className="ui-meta">No exact match, so these are the most similar images.</span>}
      </div>

      {n > 0 && (   // bulk delete: staff only (the old page's !user.isExternal); the API decides per asset
        <div className="ui-inset ui-inset--bar ui-toolbar" role="toolbar" aria-label="Selected assets">
          <span className="ui-meta">{plural(n, 'asset')} selected</span>
          <Button variant="danger" icon={Trash2} loading={bulkBusy} data-testid="assets-bulk-delete" onClick={bulkDelete}>Delete {plural(n, 'asset')}</Button>
          <Button variant="quiet" disabled={bulkBusy} onClick={() => setSelected(new Set(shown.map(a => a.id)))}>Select all loaded</Button>
          <Button variant="quiet" icon={X} disabled={bulkBusy} onClick={() => setSelected(new Set())}>Clear selection</Button>
        </div>
      )}

      <div className="ui-work">
        <Grid label="Asset library" density="medium" storageKey="hf-grid:assets"
          columns={columns} rows={gridRows} rowKey={a => a.id} selectedKey={openId}
          onOpen={a => setOpenId(a.id)} rowActions={rowActions}
          state={lib.state} empty={empty}
          error={{ title: 'The asset library did not load', message: 'Check your connection, then try again.', onRetry: lib.load }} />
        {openId && (
          <AssetPanel key={openId} assetId={openId} user={user} folders={lib.folders}
            onClose={() => setOpenId(null)}
            onArchived={lib.refresh} onDeleted={lib.refresh} onFolderChanged={lib.refresh} />
        )}
      </div>

      {lib.hasMore && lib.state === 'data' && (
        <div className="ui-inset ui-inset--bar ui-toolbar">
          <span className="ui-meta">Showing {lib.assets.length} of {lib.total}</span>
          <Button variant="secondary" loading={lib.loadingMore} data-testid="assets-more" onClick={lib.loadMore}>Load more</Button>
        </div>
      )}

      {upload.element}

      <Modal open={!!folderForm} onClose={() => setFolderForm(null)} title={folderForm?.mode === 'rename' ? 'Rename folder' : 'New folder'}
        footer={(
          <>
            <Button variant="quiet" onClick={() => setFolderForm(null)}>Cancel</Button>
            <Button variant="primary" loading={folderBusy} disabled={!(folderForm?.name || '').trim()} onClick={saveFolder}>
              {folderForm?.mode === 'rename' ? 'Rename folder' : 'Create folder'}
            </Button>
          </>
        )}>
        {folderForm && (
          <form className="ui-stack" onSubmit={e => { e.preventDefault(); saveFolder(); }}>
            <Input label="Folder name" value={folderForm.name} data-autofocus autoComplete="off"
              onChange={e => setFolderForm(f => ({ ...f, name: e.target.value }))} />
            {folderForm.mode === 'new' && lib.clients.length > 0 && (
              <Select label="Client" value={folderForm.clientId} onChange={e => setFolderForm(f => ({ ...f, clientId: e.target.value }))}
                hint="A client's folder is the one its client accounts see."
                options={[{ value: '', label: 'No client' }, ...lib.clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
            )}
          </form>
        )}
      </Modal>
    </div>
  );
}
